/**
 * The Auth0 SDK token cache, wrapped so the refresh token can be revoked when a
 * session ends.
 *
 * `logout()` clears the local cache and the Auth0 session cookie but does not
 * revoke the refresh token (auth0/auth0-spa-js#1320), so the credential stays
 * valid at Auth0 for its whole remaining lifetime. Since the SPA persists that
 * token in browser storage, an inactivity auto-logoff would otherwise bound the
 * user interface and not the credential — which is the point of the auto-logoff
 * (ESCLOUD-112).
 *
 * A custom `cache` is the supported way to reach the token: the SDK offers no
 * accessor for it, the `cache` option takes precedence over `cacheLocation`, and
 * `LocalStorageCache` is delegated to verbatim so storage behaviour is
 * unchanged. Nothing here parses the SDK's `@@auth0spajs@@` keys — that format
 * is the SDK's business and would break on an upgrade.
 */

import { LocalStorageCache, type Cacheable, type ICache } from '@auth0/auth0-react';

import { errorIds, logError } from '@/lib/log-error';
import { RUNTIME_CONFIG } from '@/lib/runtime-config';

/**
 * Created on first use rather than at module load: this module is imported
 * transitively by anything that can sign out, and constructing an SDK object at
 * import time makes it unimportable wherever the SDK is stubbed.
 */
let store: LocalStorageCache | undefined;

function inner(): LocalStorageCache {
    return (store ??= new LocalStorageCache());
}

/**
 * The refresh token last seen passing through the cache. Memory only: the token
 * is already in localStorage under the SDK's own key, so keeping a copy here
 * adds no new place for it to leak from, and it is dropped the moment it is
 * spent on a revocation.
 */
let lastSeenRefreshToken: string | null = null;

/** Cache entries are `{ body: { access_token, refresh_token?, ... }, expiresAt }`. */
function observe(entry: unknown): void {
    const token = (entry as { body?: { refresh_token?: unknown } } | undefined)?.body?.refresh_token;
    if (typeof token === 'string' && token.length > 0) {
        lastSeenRefreshToken = token;
    }
}

/**
 * Observes both directions on purpose: `set` catches each rotation, and `get`
 * catches the token restored from storage on a page load, which is the case
 * where the user signs out without ever having refreshed.
 */
export const revocableTokenCache: ICache = {
    get<T = Cacheable>(key: string) {
        const entry = inner().get<T>(key);
        observe(entry);
        return entry;
    },
    set(key: string, entry: unknown) {
        observe(entry);
        return inner().set(key, entry);
    },
    remove(key: string) {
        return inner().remove(key);
    },
    allKeys() {
        return inner().allKeys();
    },
};

/** Exposed for tests; the sign-out path has no reason to read this. */
export function peekRefreshToken(): string | null {
    return lastSeenRefreshToken;
}

/**
 * Revoke the current refresh token at Auth0. Never throws and never blocks:
 * failing to revoke must not trap a user in a session they asked to leave, and
 * the shortened token lifetimes remain the backstop if this does not land.
 */
export async function revokeRefreshToken(): Promise<void> {
    const token = lastSeenRefreshToken;
    lastSeenRefreshToken = null;

    if (!token) return;

    try {
        // The revocation is spent on the login domain, the same one that issued
        // the token. `keepalive` is what lets it outlive the navigation that
        // `logout()` starts immediately afterwards instead of being cancelled.
        const response = await fetch(`https://${RUNTIME_CONFIG.auth0Domain}/oauth/revoke`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ client_id: RUNTIME_CONFIG.auth0ClientId, token }),
            keepalive: true,
        });

        if (!response.ok) {
            logError(
                errorIds.REFRESH_TOKEN_REVOKE_FAILED,
                `Auth0 refused the refresh-token revocation (HTTP ${response.status}). The token stays valid until its lifetime expires.`,
                { status: response.status }
            );
        }
    } catch (error) {
        logError(
            errorIds.REFRESH_TOKEN_REVOKE_FAILED,
            'Could not reach Auth0 to revoke the refresh token at sign-out. The token stays valid until its lifetime expires.',
            { error }
        );
    }
}
